import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import dayjs from 'dayjs';
import { IResidentDFreeGraph } from '@/types/residents/dfreeGraph';
import { ResidentListHeader } from '@/components/residentListHeader';
import { DfreeGraphListItem } from './dfreeGraphListItem';
import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import { residentListSettingsSelector } from '@/redux/config/residentListSettings';
import { useLoading } from '@/contexts/LoadingContext';
import { RecordDialog } from '@/components/careRecord/recordDialog';
import { RecordModal } from '@/components/careRecord/recordModal';
import { navSelector, navSlice } from '@/redux/config/leftnav';
import { selfDataSelector } from '@/redux/config/selfData';
import { useSortResidents } from '@/hooks/useSortResidents';
import { SortType } from '@/types/components';
import { BatteryAlertModal } from '@/components/batteryAlertModal';
import { useFetchDFreeGraphList } from '@/hooks/multi/useFetchDFreeGraphList';
import { RawGraphCheckbox } from './rawGraphCheckbox';
import { DataAnomalyHintButton } from '../dataAnomalyHintButton';

const Container = css`
  padding: 0 16px;
`;

const EmptyResidentContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 16px;
`;

const createGraphDateRange = () => {
  return {
    start: formatUtcFetchTimestamp(dayjs().subtract(5, 'hour')),
    end: formatUtcFetchTimestamp(dayjs()),
  };
};

export const ProDfreeGraphList: React.FC = () => {
  const dispatch = useDispatch();
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { badge } = useSelector(navSelector);
  const residentListSettings = useSelector(residentListSettingsSelector);
  const self = useSelector(selfDataSelector);
  const [resident, setResident] = React.useState<IResidentDFreeGraph>();
  const { sortedResidents, sortResidents } = useSortResidents<IResidentDFreeGraph>();
  const { fetchDFreeGrapListData } = useFetchDFreeGraphList();
  const [batteryAlertResident, setBatteryAlertResident] = React.useState<IResidentDFreeGraph | null>(null);
  const [gasBtteryAlertResident, setGasBtteryAlertResident] = React.useState<IResidentDFreeGraph | null>(null);
  const [isRawGraphVisible, setRawGraphVisible] = React.useState(false);
  const [dateRange, setDateRange] = React.useState<{ start: string; end: string }>(() => createGraphDateRange());
  const { showLoading, hideLoading } = useLoading();
  const { t, i18n } = useTranslation();
  const isJa = i18n.language.startsWith('ja');

  const createFilterType = (condition: 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit') => {
    if (['deviceAssigned', 'deviceAttached'].includes(condition)) {
      return condition as 'deviceAssigned' | 'deviceAttached';
    } else return null;
  };

  const fetchResidents = React.useCallback(
    async (unitId?: number | null, condition?: 'deviceAssigned' | 'deviceAttached' | null, sortType?: SortType) => {
      const { start, end } = createGraphDateRange();
      setDateRange({ start, end });
      showLoading();

      try {
        const residents: any[] = await fetchDFreeGrapListData(unitId, condition ?? null, start, end);

        // 入居者一覧を並び替え
        sortResidents(residents, sortType ?? residentListSettings.sortType);
        hideLoading();

        // 本体機器異常検知
        setBatteryAlertResident(residents.find((v) => v.batteryAlertNotification));
        setGasBtteryAlertResident(residents.find((v) => v.gasBatteryAlertNotification));
      } catch (error: any) {
        console.error('fetchResidents error', error);
        hideLoading();
      }
    },
    [sortedResidents, residentListSettings, nursingHomeId],
  );

  const UpdateBatteryAlert = async (residentId?: number) => {
    setBatteryAlertResident(null);
    if (residentId) {
      await api.put('/residents/:residentId/notifications/covers', {
        residentId: residentId,
        uncaredBatteryAlert: false,
      });
      fetchResidents(
        residentListSettings.unitId,
        createFilterType(residentListSettings.conditions),
        residentListSettings.sortType,
      );
    }
  };

  const UpdateGasBatteryAlert = async (residentId?: number) => {
    setGasBtteryAlertResident(null);
    if (residentId) {
      await api.put('/residents/:residentId/notifications/covers', {
        residentId: residentId,
        uncaredGasBatteryAlert: false,
      });
      fetchResidents(
        residentListSettings.unitId,
        createFilterType(residentListSettings.conditions),
        residentListSettings.sortType,
      );
    }
  };

  const handleOpenRecordModal = React.useCallback((resident: IResidentDFreeGraph) => {
    setResident(resident);
  }, []);

  const handleCloseRecordModal = React.useCallback(() => {
    setResident(undefined);
  }, []);

  const handleUpdate = React.useCallback(() => {
    fetchResidents(
      residentListSettings.unitId,
      createFilterType(residentListSettings.conditions),
      residentListSettings.sortType,
    );
  }, [residentListSettings, nursingHomeId]);

  React.useEffect(() => {
    if (!self.selfUser.id) return;
    let unitId = null;

    let conditions =
      (localStorage.getItem('condition') as 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit') ?? 'all';
    // 前回と異なるIDの場合はデフォルト値
    if (self.selfUser.id !== Number(localStorage.getItem('self')) && !self.selfUser.nursingHomeUnitId) {
      unitId = null;
      conditions = 'all';
    } else if (self.selfUser.nursingHomeUnitId) {
      unitId = self.selfUser.nursingHomeUnitId;
    } else if (!localStorage.getItem('unit') || localStorage.getItem('unit') === 'null') {
      unitId = null;
    } else {
      unitId = localStorage.getItem('unit');
    }
    fetchResidents(unitId ? +unitId : null, createFilterType(conditions));
  }, [self.selfUser.id, nursingHomeId]);

  React.useEffect(() => {
    const timer = setInterval(
      () => {
        handleUpdate();
      },
      1000 * 60 * 5,
    );
    return () => {
      clearInterval(timer);
    };
  }, [residentListSettings, nursingHomeId]);

  // 通知アラートの消去とバッヂカウントの更新処理
  const handleResetNotification = React.useCallback(
    async (residentId: number, coverTypes?: string[]) => {
      let params = { residentId } as {
        residentId: number;
        uncaredBigBladder?: boolean;
        uncaredUrination?: boolean;
        uncaredGasPooped?: boolean;
        uncaredGasPrediction?: boolean;
        uncaredGotUp?: boolean;
        uncaredSensorError?: boolean;
        uncaredUrineReset?: boolean;
        uncaredZeroReset?: boolean;
        uncaredUpsideDown?: boolean;
        uncaredMoveUpperAdvice?: boolean;
        uncaredMoveLowerAdvice?: boolean;
      };

      const coverTypeMap: Record<string, keyof typeof params> = {
        bigBladder: 'uncaredBigBladder',
        urination: 'uncaredUrination',
        gasPooped: 'uncaredGasPooped',
        gasPrediction: 'uncaredGasPrediction',
        gotUp: 'uncaredGotUp',
        sensorError: 'uncaredSensorError',
        urineReset: 'uncaredUrineReset',
        zeroReset: 'uncaredZeroReset',
        upsideDown: 'uncaredUpsideDown',
        moveUpperAdvice: 'uncaredMoveUpperAdvice',
        moveLowerAdvice: 'uncaredMoveLowerAdvice',
      };

      const selectedCoverTypes = coverTypes ?? Object.keys(coverTypeMap);
      params = selectedCoverTypes.reduce((acc, type) => {
        const paramKey = coverTypeMap[type] as keyof typeof params;
        return paramKey ? { ...acc, [paramKey]: false } : acc;
      }, params);

      await api.put('/residents/:residentId/notifications/covers', params);
      if (badge.resident > 0) {
        dispatch(navSlice.actions.setResidentBadge(-1));
      }
      fetchResidents(residentListSettings.unitId, createFilterType(residentListSettings.conditions));
    },
    [residentListSettings, badge, nursingHomeId],
  );

  return (
    <div css={Container}>
      <ResidentListHeader
        title={t('dfreeGraph', 'DFreeグラフ')}
        conditions={residentListSettings.conditions}
        sortType={residentListSettings.sortType}
        unitId={residentListSettings.unitId}
        rightOfUpdateElement={isJa ? <DataAnomalyHintButton /> : null}
        onUpdate={handleUpdate}
        onChangeListSettings={fetchResidents}
        rightElement={
          <RawGraphCheckbox
            checked={isRawGraphVisible}
            onChange={(checked) => {
              setRawGraphVisible(checked);
            }}
          />
        }
      />
      {sortedResidents ? (
        <>
          {sortedResidents.length > 0 ? (
            <>
              {sortedResidents.map((resident, i) => (
                <DfreeGraphListItem
                  key={resident.id}
                  resident={resident}
                  fetch={handleUpdate}
                  dateRange={dateRange}
                  openDialog={handleOpenRecordModal}
                  resetNotification={handleResetNotification}
                  showRawGraph={isRawGraphVisible}
                />
              ))}
            </>
          ) : (
            <div css={EmptyResidentContainer}>{t('thereAreNoResidents', '入居者がいません')}</div>
          )}
        </>
      ) : (
        <></>
      )}
      {resident && (
        <RecordDialog isOpen={!!resident} dialogWidth={'640px'} onClose={handleCloseRecordModal}>
          <RecordModal
            resident={resident}
            resetNotification={handleResetNotification}
            onClose={handleCloseRecordModal}
            onUpdate={handleUpdate}
          />
        </RecordDialog>
      )}
      <BatteryAlertModal
        isOpen={!!batteryAlertResident}
        name={batteryAlertResident?.name}
        title={
          <div>
            {t('anAbnormalityHasBeenDetectedInTheDFreeDevice', 'DFree機器本体の異常を検知しました。')}
            <br />
            {t('pleaseStopUsingItAndContactCustomerSupport', '使用を中止し、サポート窓口にお問い合わせください。')}
          </div>
        }
        onClose={() => {
          UpdateBatteryAlert(batteryAlertResident?.id);
        }}
      >
        <div>
          {t('833-337-3387(833-DFREEUS)', '【電話番号】03-5459-1295')}
          <br />
          {t('mon-Fri9AM-5PMPST', '【受付時間】平日10:00~17:00')}
          <br />（{t('excludingWeekendAndHolidays', '土日祝日・年末年始を除く')}）
        </div>
      </BatteryAlertModal>
      <BatteryAlertModal
        isOpen={!!gasBtteryAlertResident}
        name={gasBtteryAlertResident?.name}
        title={
          <div>
            {t('anAbnormalityHasBeenDetectedInDFreeBenDevice', 'DFreeBenの異常を検知しました。')}
            <br />
            {t('pleaseStopUsingItAndContactCustomerSupport', '使用を中止し、サポート窓口にお問い合わせください。')}
          </div>
        }
        onClose={() => {
          UpdateGasBatteryAlert(gasBtteryAlertResident?.id);
        }}
      >
        <div>
          {t('833-337-3387(833-DFREEUS)', '【電話番号】03-5459-1295')}
          <br />
          {t('mon-Fri9AM-5PMPST', '【受付時間】平日10:00~17:00')}
          <br />（{t('excludingWeekendAndHolidays', '土日祝日・年末年始を除く')}）
        </div>
      </BatteryAlertModal>
    </div>
  );
};
